import styled from '@emotion/styled';
import { colors, MuiThemeProvider, Theme, Typography } from '@material-ui/core';
import { ChevronRight } from '@material-ui/icons';
import { DateTime } from 'luxon';
import { ClassAttributes, createElement, Fragment, ReactNode } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';

import { SingleOfferFragment } from '../../../../graphql/generated/graphql_offer';
import theme from '../../../../theme';
import { toJSDate } from '../../../../utility/graphql/datetime';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import _OfferDeadlineDisplay from '../OfferDeadlineDisplay';
import OfferProposedCount from '../OfferProposedCount';
import _OfferRewardDisplay from '../OfferRewardDisplay';
import OfferRewardQuota from '../OfferRewardQuota';
import _OfferStatus from '../OfferStatus';
import OfferWorkerStatusLabel from '../OfferWorkerStatusLabel';
import CreativeFormatListItem from './CreativeFormatListItem';

export interface Props {
  offer: Pick<
    SingleOfferFragment,
    | 'jobId'
    | 'availableFrom'
    | 'availableTo'
    | 'rewardCurrency'
    | 'rewardAmount'
    | 'rewardQuota'
    | 'expectedDue'
    | 'workerStatus'
    | 'assignmentCount'
    | 'creativeFormats'
    | 'expressDelivery'
    | 'matchingFormat'
    | 'candidateCount'
  >;
  currentDateTime?: DateTime;
  action?: ReactNode;
  clickable?: boolean;
  className?: string;
}

// Because mui Button doesn't has 'warn' color, actionTheme defines 'secondary' as warn color.
const actionsTheme = (parent: Theme): Theme => ({
  ...parent,
  palette: {
    ...parent.palette,
    secondary: {
      dark: colors.red[700],
      main: colors.red[500],
      light: colors.red[300],
      contrastText: '#FFF',
    },
  },
});

const OfferDisplay = ({
  offer,
  currentDateTime = DateTime.local(),
  action = null,
  clickable = false,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const assignableQuota = offer.rewardQuota - offer.assignmentCount;
  const openingState =
    assignableQuota === 0 || currentDateTime > DateTime.fromISO(offer.availableTo)
      ? 'CLOSED'
      : currentDateTime < DateTime.fromISO(offer.availableFrom)
      ? 'BEFORE_OPEN'
      : 'OPENING';

  const resizeBaseCreationList = offer.creativeFormats.filter((creativeFormat) => !creativeFormat.resizeBaseId);
  let itemCount = 1;

  const creationList = resizeBaseCreationList.map((creativeFormat, index) => {
    return (
      <CreativeFormatWithResizeItem key={`offer_display.creative_formats.${index}`}>
        <CreativeFormatListItem
          key={`offer_display.creative_format_list.${creativeFormat.id}`}
          creativeFormat={creativeFormat}
          itemIndex={itemCount++}
        />

        {offer.creativeFormats.map((resizeCreativeFormat) => {
          return creativeFormat.id === resizeCreativeFormat.resizeBaseId ? (
            <CreativeFormatListItem
              key={`offer_display.resize_creative_format_list.${resizeCreativeFormat.id}`}
              creativeFormat={resizeCreativeFormat}
              itemIndex={itemCount++}
            />
          ) : null;
        })}
      </CreativeFormatWithResizeItem>
    );
  });

  return (
    <Root className={className}>
      <Content>
        <OfferInfo>
          <OfferReward
            offer={{
              rewardCurrency: offer.rewardCurrency,
              rewardAmount: offer.rewardAmount,
            }}
            isAvailable={assignableQuota > 0 && openingState !== 'CLOSED'}
            displayRewardText={offer.matchingFormat == 'competition'}
          />
          <OfferStatus
            offer={{
              availableFrom: toJSDate(offer.availableFrom),
              availableTo: toJSDate(offer.availableTo),
            }}
            openingState={openingState}
          />
          <Divider />
          <OfferDeadlineDisplay
            offer={{
              expressDelivery: offer.expressDelivery,
              expectedDue: toJSDate(offer.expectedDue),
            }}
          />
          <Divider />
          {offer.matchingFormat == 'competition' ? (
            <OfferProposedCount rewardQuota={offer.rewardQuota} candidateCount={offer.candidateCount} />
          ) : (
            <OfferRewardQuota rewardQuota={offer.rewardQuota} />
          )}

          <MuiThemeProvider theme={(parent) => actionsTheme(parent! as Theme)}>
            <OfferAction>{action}</OfferAction>
          </MuiThemeProvider>
        </OfferInfo>

        {offer.workerStatus && offer.matchingFormat == 'auto' && (
          <OfferWorkerStatus>
            <OfferWorkerStatusLabel workerStatus={offer.workerStatus} />
          </OfferWorkerStatus>
        )}

        {creationList.length >= 1 ? (
          <CreationListHeading>
            <FormattedMessage id="offer_display.creation_list_item.heading" />
          </CreationListHeading>
        ) : null}

        <CreationList>{creationList}</CreationList>

        {offer.matchingFormat == 'competition' && (
          <Fragment>
            <CompetitionText align="right">
              <FormattedHTMLMessage id="offer_display.creation_list_item.help.text" />
            </CompetitionText>
          </Fragment>
        )}
      </Content>

      {clickable ? (
        <Indicator>
          <ChevronRight />
        </Indicator>
      ) : null}
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['1fr', 'auto'],
    rows: ['auto'],
    areas: [['content', 'indicator']],
    alignItems: 'center',
  }),
  padding: `${SPACING_REGULAR}px ${SPACING_UNIT * 3}px`,
});

const Content = styled('div')(gridLayoutItem('content'));

const Indicator = styled('div')(gridLayoutItem('indicator'), {
  marginLeft: SPACING_UNIT * 3,
});

const Divider = styled('div')({
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  height: 28,
  marginRight: SPACING_UNIT,
  marginLeft: SPACING_UNIT,
});

const OfferInfo = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'row',
    alignItems: 'center',
  }),
  marginBottom: SPACING_UNIT,
});

const OfferJobId = styled(Typography)({
  marginBottom: SPACING_UNIT,
});
OfferJobId.defaultProps = { variant: 'caption' };

const OfferReward = styled(_OfferRewardDisplay)({
  ...gridLayoutItem('reward'),
  display: 'inline-block',
  minWidth: 152,
  marginRight: SPACING_UNIT,
});

const OfferStatus = styled(_OfferStatus)({
  ...gridLayoutItem('offerStatus'),
});

const OfferWorkerStatus = styled('div')({
  marginBottom: SPACING_UNIT,
});

const OfferDeadlineDisplay = styled(_OfferDeadlineDisplay)({
  ...gridLayoutItem('deadline'),
});

const OfferAction = styled('div')({
  marginLeft: SPACING_REGULAR,
});

const CreationListHeading = styled(Typography)({
  fontSize: 12,
  marginBottom: SPACING_UNIT,
});

const CompetitionText = styled(Typography)({
  fontSize: 12,
  margin: `${SPACING_UNIT}px 0px`,
  color: '#ec1600',
});

const CreationList = styled('div')({
  ...gridLayoutItem('creationList'),
});

const CreativeFormatWithResizeItem = styled('div')({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  ':last-child': {
    borderBottomWidth: 0,
  },
});

export default OfferDisplay;
